import type { ReactNode } from 'react';
import { deviceT } from '../messages';
import type { T } from '../messages';
import type { StaffBranding } from '../wire';

/**
 * **The personality, ported.** (`docs/lovespa-personality.md`.)
 *
 * Everything in this file exists because an owner spent real effort making
 * LoveSpa feel like LoveSpa, and cutover day has to feel like LoveSpa got
 * *better* rather than like it got replaced by something generic. Three touches
 * live here — the wordmark, the gold-gradient heart, and the plus it replaces —
 * and each of them is drawn from `../../../LoveSpa/src`, character for
 * character where the shape is the point.
 *
 * What is deliberately *not* here: any way for a tenant to supply their own
 * markup, their own SVG or their own CSS. Spec §6.4.3's principle — consistency
 * is what makes support possible — survives the personality wave intact. The
 * tenant picks from a named set (`StaffBranding`); the bundle owns the drawing.
 */

/* ------------------------------------------------------------------ */
/* The gold-gradient heart                                             */
/* ------------------------------------------------------------------ */

/**
 * LoveSpa's heart, ported faithfully — and the port has two halves, because the
 * original does.
 *
 * In LoveSpa the heart is drawn **twice**, in two different technologies, for
 * one reason each:
 *
 *  - `styles.css` ~280: the floating add button (`.fab`) paints the heart on
 *    `::before` with `clip-path: path('M47.2 0c-6.8 0-12.6 5.4-15.2 11.2…')`
 *    over a `linear-gradient(140deg, #dcb765, #b8903a)`. Its own comment says
 *    why: *"drop-shadow on the button follows the heart outline (box-shadow
 *    wouldn't)"*. The shape is a clip so the glow under it is heart-shaped.
 *  - `Icons.tsx` :85: `GoldHeartIcon`, an inline SVG on a `0 0 32 30` viewBox
 *    with a `linearGradient` from `#dcb765` to `#b8903a`, used inline in prose
 *    ("Good morning ♥") where a clipped pseudo-element could not go.
 *
 * The two paths are the *same drawing at two scales* — the clip-path is on a
 * 64×60 box and the SVG on 32×30, and every coordinate is exactly doubled.
 *
 * **This component is the SVG one, and it is the only one carried over.** The
 * clip-path exists in LoveSpa because a floating 64×60 button *was* the heart;
 * this app's affordances are labelled toolbar buttons that a heart sits inside,
 * so a second copy of the path would be a drawing nothing draws. What did carry
 * over from that rule is its point — the glow follows the heart's outline rather
 * than a box — as `drop-shadow` on this SVG in `styles.css`.
 *
 * The numbers below are the numbers in `Icons.tsx`. It is a port, not a redraw.
 *
 * The gradient id is suffixed per instance. LoveSpa could hardcode
 * `id="gold-heart-fill"` because one screen showed one heart; this app can show
 * a heart on a button, in a heading and in an empty state at once, and two
 * elements sharing a `<defs>` id in one document is a bug that only appears
 * when the first one unmounts.
 */
let heartSeq = 0;

export function GoldHeartIcon({ size = 20 }: { size?: number }): ReactNode {
  // Not `useId()`: this component is called from module scope in a few places
  // (an icon inside a string of prose) and a counter is enough — the value only
  // has to be unique within one document.
  heartSeq += 1;
  const gradientId = `ys-gold-heart-${String(heartSeq)}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 30"
      aria-hidden="true"
      className="gold-heart"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#dcb765" />
          <stop offset="1" stopColor="#b8903a" />
        </linearGradient>
      </defs>
      <path
        d="M23.6 0c-3.4 0-6.3 2.7-7.6 5.6C14.7 2.7 11.8 0 8.4 0 3.8 0 0 3.8 0 8.4c0 9.4 9.5 11.9 16 21.2 6.1-9.3 16-12.1 16-21.2C32 3.8 28.2 0 23.6 0z"
        fill={`url(#${gradientId})`}
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* The action icon — one choice, three affordances                     */
/* ------------------------------------------------------------------ */

export type ActionIcon = 'plus' | 'heart';

/** `action_icon`, with the default stated in one place rather than five. */
export function actionIconOf(branding: StaffBranding | undefined): ActionIcon {
  return branding?.action_icon === 'heart' ? 'heart' : 'plus';
}

/**
 * The glyph an add/walk-in/issue button wears — **and `null` on the default.**
 *
 * In LoveSpa every one of these was `<button className="fab">+</button>` with
 * the heart supplied by CSS, so the *button* was the heart and the `+` under the
 * clip was never seen. This app's equivalents are labelled buttons in a toolbar
 * — "Walk-in", "New", "Issue" — with no glyph at all, and that is what
 * `action_icon: "plus"` (and an absent document) has to keep rendering: not a
 * plus this app never had, but **exactly the button that is there today**.
 *
 * So the plus branch renders nothing. That is not a stub; it is the rule the
 * whole named set is built on stated in one component — absent config paints
 * today's DOM, byte for byte, and a choice is the only thing that adds markup.
 */
export function ActionGlyph({ icon, size = 18 }: { icon: ActionIcon; size?: number }): ReactNode {
  if (icon !== 'heart') return null;
  return <GoldHeartIcon size={size} />;
}

/**
 * **Copy follows the icon**, which is the point the personality doc makes with
 * `CertificatesView.tsx` — its empty state reads *"No outstanding certificates —
 * issue one with the heart button."*
 *
 * A tenant on the heart gets "the heart button"; a tenant on the plus gets
 * "the + button", and the callers below use that half only where a sentence
 * pointing at a glyphless toolbar button would be *new copy on the default
 * look*. The named set adds; it never re-opens a default. One function either
 * way, so an empty state can never drift into describing an affordance that is
 * not on the screen.
 *
 * **The words live in the catalog, and the personality survives translation
 * intact** — `el botón del corazón` / `el botón +`. The icon and the phrase that
 * names it are chosen by the same `if` on the same line, so they cannot end up
 * in different languages or describing different glyphs. `t` defaults to the
 * device's so a caller that has no `t` to hand still gets a real sentence.
 */
export function actionButtonName(icon: ActionIcon, t: T = deviceT): string {
  return icon === 'heart' ? t('brand.heartButton') : t('brand.plusButton');
}

/* ------------------------------------------------------------------ */
/* The wordmark                                                        */
/* ------------------------------------------------------------------ */

/**
 * LoveSpa's `BrandMark.tsx`, ported with its rule intact.
 *
 * The original's own comment: *"Recreates the Love Spa Frenchtown wordmark from
 * the configured business name: first word large in gold serif, second word
 * between hearts, the rest letterspaced below. Names that don't fit the pattern
 * render as one line."*
 *
 * So `LOVE / ♥ spa ♥ / frenchtown` is not three configured strings — it is one
 * tenant name, split. That is what makes `brandmark: "hearts"` a *named choice*
 * rather than a content slot: a tenant turns it on and their own name arranges
 * itself, and a tenant whose name is two words gets the plain bar automatically
 * because the pattern does not fit. There is nowhere here to type a different
 * wordmark, which is the whole difference between a named set and a fork.
 *
 * The hearts are the character `♥`, exactly as in the original — not an SVG.
 * They sit in flowing text at 0.75rem and an icon would have to be baseline-
 * aligned by hand at every size.
 */
export function BrandMark({ name, style }: { name: string; style: 'plain' | 'hearts' }): ReactNode {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w !== '');

  if (style !== 'hearts' || words.length < 3) {
    return <span className="brandmark brandmark--plain">{name}</span>;
  }

  const [first, second, ...rest] = words as [string, string, ...string[]];
  return (
    <span className="brandmark brandmark--hearts">
      <span className="brandmark__main">{first}</span>
      <span className="brandmark__sub">
        <span className="brandmark__heart">♥</span> {second.toLowerCase()}{' '}
        <span className="brandmark__heart">♥</span>
      </span>
      {rest.length === 0 ? null : (
        <span className="brandmark__bottom">{rest.join(' ').toLowerCase()}</span>
      )}
    </span>
  );
}
